/**
 * Import a `.glayer` archive into a gTwin scene (WP-E).
 *
 * Platform: POST /api/v1/gtwins/{gtwinId}/scene/items/import
 */

function apiBase(): string {
  const fromEnv =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_GTWIN_API_URL : undefined;
  if (fromEnv && String(fromEnv).trim()) return String(fromEnv).replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/gtwin-api`;
  }
  return 'http://127.0.0.1:5173/gtwin-api';
}

function tenantId(): string {
  const raw =
    typeof import.meta !== 'undefined' ? import.meta.env?.VITE_DEFAULT_TENANT : undefined;
  return raw && String(raw).trim() ? String(raw) : 'dev-tenant';
}

export interface GlayerImportResult {
  readonly id: string;
  readonly version: number;
  readonly item: Record<string, unknown>;
  readonly objectsIngested: number;
}

export async function importGlayerFile(
  gtwinId: string,
  file: File,
  updatedBy = 'geoviewer',
): Promise<GlayerImportResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  const url = `${apiBase()}/api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/items/import?by=${encodeURIComponent(updatedBy)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'X-Tenant-Id': tenantId() },
    body: form,
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Glayer import failed (${res.status}): ${detail.slice(0, 240)}`);
  }
  const body = (await res.json()) as {
    id?: string;
    version?: number;
    item?: Record<string, unknown>;
    objectsIngested?: number;
  };
  return {
    id: body.id ?? gtwinId,
    version: body.version ?? 0,
    item: body.item ?? {},
    objectsIngested: body.objectsIngested ?? 0,
  };
}
